import React, { useState, useEffect, useRef } from "react";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { formatCPF, maskDateBRLive, brToISO } from "../../lib/utils";
import { useThemeStore } from "../../store/useThemeStore";
import { WhatsAppSupportDialog } from "../ui/WhatsAppSupportDialog";
import { authService } from "../../services/auth.service";

function isCpfLike(val) {
  return /^[\d.\-\s]+$/.test(val.replace(/\s/g, ""));
}

function Login({ onLogin, isLoading, initialCpf = "", initialTipo = "" }) {
  const { theme } = useThemeStore();
  const loginTitle    = theme.login_title    || "Acesso Restrito";
  const loginSubtitle = theme.login_subtitle || "Insira suas credenciais para continuar";

  const [identifier, setIdentifier] = useState(initialCpf);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [dataNascDisplay, setDataNascDisplay] = useState("");
  const [dobError, setDobError] = useState("");
  const [autoLoginPending, setAutoLoginPending] = useState(!!initialCpf);
  const [wppDialogOpen, setWppDialogOpen] = useState(false);
  const [wppCpf, setWppCpf] = useState("");
  const [wppDataNasc, setWppDataNasc] = useState("");
  const [wppErrorType, setWppErrorType] = useState("");

  const cpfDigits = identifier.replace(/\D/g, "");
  const [prefetchedCedente, setPrefetchedCedente] = useState(null);
  const prefetchCpfRef = useRef("");

  useEffect(() => {
    if (cpfDigits.length !== 11 || showPassword) {
      setPrefetchedCedente(null);
      prefetchCpfRef.current = "";
      return;
    }
    if (prefetchCpfRef.current === identifier) return;
    prefetchCpfRef.current = identifier;
    authService.login(identifier).then(data => {
      if (prefetchCpfRef.current === identifier) setPrefetchedCedente(data);
    }).catch(() => {
      if (prefetchCpfRef.current === identifier) setPrefetchedCedente(null);
    });
  }, [cpfDigits.length, identifier, showPassword]);

  const requiresDOB = prefetchedCedente?.processos_cedente?.some(
    pc => pc.processo?.requer_data_nascimento === true
  ) ?? false;

  const showDOB = cpfDigits.length === 11 && !showPassword && requiresDOB;

  useEffect(() => {
    if (initialCpf) {
      onLogin(initialCpf, initialTipo).finally(() => setAutoLoginPending(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIdentifierChange = (e) => {
    const val = e.target.value;
    if (/^[\d.-]*$/.test(val)) {
      setIdentifier(formatCPF(val));
      setShowPassword(false);
      setPassword("");
    } else {
      setIdentifier(val);
      setShowPassword(!isCpfLike(val) && val.trim().length >= 3);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) return;

    const isoDate = showDOB ? brToISO(dataNascDisplay) : null;
    if (showDOB && dataNascDisplay.length > 0 && !isoDate) {
      setDobError("Data inválida");
      return;
    }
    setDobError("");

    const res = await (showPassword
      ? onLogin(identifier.trim(), password)
      : onLogin(identifier.trim(), initialTipo, isoDate));

    if (!res?.success && theme?.whatsapp_suporte) {
      if (res?.error?.includes("não encontrado")) {
        setWppCpf(identifier.trim());
        setWppDataNasc("");
        setWppErrorType("not_found");
        setWppDialogOpen(true);
      } else if (res?.error === "data_nascimento_incorreta") {
        setWppCpf(identifier.trim());
        setWppDataNasc(dataNascDisplay);
        setWppErrorType("dob_mismatch");
        setWppDialogOpen(true);
      } else if (res?.error === "valor_zero") {
        setWppCpf(identifier.trim());
        setWppDataNasc("");
        setWppErrorType("valor_zero");
        setWppDialogOpen(true);
      }
    }
  };

  if (autoLoginPending) {
    return (
      <div className="flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-5 sm:p-7 md:p-10 shadow-sm border border-border bg-white overflow-hidden relative">
          <div className="absolute top-0 left-0 w-1 h-full bg-primary" />
          <div className="flex flex-col items-center gap-4 py-4">
            <svg className="animate-spin h-8 w-8 text-muted-foreground" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
            <p className="text-muted-foreground text-sm font-medium">Autenticando...</p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
    <WhatsAppSupportDialog
      isOpen={wppDialogOpen}
      onOpenChange={setWppDialogOpen}
      cpf={wppCpf}
      dataNascimento={wppDataNasc}
      errorType={wppErrorType}
      phone={theme?.whatsapp_suporte}
    />
    <div className="flex items-center justify-center p-4 w-full">
      <Card className="max-w-md w-full p-5 sm:p-7 md:p-10 shadow-sm border border-border bg-white overflow-hidden relative">
        <div className="absolute top-0 left-0 w-1 h-full bg-primary" />

        <div className="mb-6 sm:mb-10 text-center">
          <h1 className="text-xl sm:text-2xl font-black text-[#212121] tracking-tight uppercase italic">
            {loginTitle.includes(" ") ? (
              <>
                {loginTitle.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="text-primary">{loginTitle.split(" ").slice(-1)}</span>
              </>
            ) : (
              <span className="text-primary">{loginTitle}</span>
            )}
          </h1>
          <p className="text-[#616161] text-xs font-medium mt-1">{loginSubtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label
              htmlFor="login-input"
              className="text-[11px] font-bold text-[#616161] uppercase tracking-widest pl-1"
            >
              CPF
            </Label>
            <Input
              type="text"
              id="login-input"
              value={identifier}
              onChange={handleIdentifierChange}
              placeholder="000.000.000-00"
              disabled={isLoading}
              autoComplete="username"
              className="border-2 border-[#e0e0e0] focus:border-primary transition-all h-12 text-[#333333] font-medium focus:ring-4 focus:ring-primary/10"
            />
          </div>

          {showDOB && (
            <div className="space-y-2">
              <Label
                htmlFor="dob-input"
                className="text-[11px] font-bold text-[#616161] uppercase tracking-widest pl-1"
              >
                Data de Nascimento
              </Label>
              <Input
                type="text"
                id="dob-input"
                value={dataNascDisplay}
                onChange={(e) => {
                  setDobError("");
                  setDataNascDisplay(maskDateBRLive(e.target.value));
                }}
                placeholder="DD/MM/AAAA"
                maxLength={10}
                inputMode="numeric"
                disabled={isLoading}
                className={`border-2 transition-all h-12 text-[#333333] font-medium focus:ring-4 ${
                  dobError
                    ? "border-red-400 focus:border-red-400 focus:ring-red-300/10"
                    : "border-[#e0e0e0] focus:border-primary focus:ring-primary/10"
                }`}
              />
              {dobError && <p className="text-xs text-red-500 pl-1">{dobError}</p>}
            </div>
          )}

          {showPassword && (
            <div className="space-y-2">
              <Label
                htmlFor="password-input"
                className="text-[11px] font-bold text-[#616161] uppercase tracking-widest pl-1"
              >
                Senha
              </Label>
              <Input
                type="password"
                id="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                disabled={isLoading}
                autoComplete="current-password"
                className="border-2 border-[#e0e0e0] focus:border-primary transition-all h-12 text-[#333333] font-medium focus:ring-4 focus:ring-primary/10"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-12 text-sm font-black bg-primary hover:bg-primary/85 text-primary-foreground shadow-lg transition-all active:scale-[0.98] uppercase tracking-widest"
          >
            {isLoading ? (
              <span className="inline-flex items-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                </svg>
                Validando...
              </span>
            ) : (
              "Entrar no Sistema"
            )}
          </Button>
        </form>
      </Card>
    </div>
    </>
  );
}

export default Login;
